import {
	type FormEvent,
	type KeyboardEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useAuth } from "../../auth/AuthContext";
import { Modal } from "../../components/Modal";
import {
	getSchedule,
	type SaveScheduleVisitInput,
	saveScheduleVisit,
	saveStaffUnavailability,
} from "../../lib/api";
import { cn } from "../../lib/cn";
import {
	formatDate,
	formatStaffHours,
	isoToday,
	monthBounds,
	monthGrid,
	shiftDate,
	shiftMonth,
	weekBounds,
	weekGrid,
} from "./calendar";
import styles from "./SchedulingPage.module.css";
import { VisitPreviewCard } from "./VisitPreviewCard";
import type {
	PlannerJob,
	ScheduleData,
	ScheduledVisit,
	SchedulingPerson,
	StaffUnavailability,
} from "./types";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** Only ever one card is open, so one id serves every chip's aria-describedby. */
const PREVIEW_ID = "visit-preview";

/** How a calendar chip asks for its preview card. */
interface PreviewApi {
	activeId: string | null;
	/** A pointer arrived: open after a short intent delay. */
	hover: (visitId: string, element: HTMLElement) => void;
	endHover: () => void;
	/** Open at once — keyboard focus has already shown intent. */
	show: (visitId: string, element: HTMLElement) => void;
	/** Touch: open, or close if this chip's card is already the open one. */
	toggle: (visitId: string, element: HTMLElement) => void;
	close: () => void;
}

const KIND_LABELS: Record<string, string> = {
	user: "Staff",
	contractor: "Contractor",
};

const STATUS_LABELS: Record<ScheduledVisit["status"], string> = {
	planned: "Planned",
	tentative: "Tentative",
	cancelled: "Cancelled",
};

function assignmentPersonLabel(
	assignment: ScheduledVisit["assignments"][number],
): string {
	const name = assignment.personName || "Staff member";
	if (assignment.identityStatus === "inactive") return `${name} (inactive)`;
	if (assignment.identityStatus === "missing") {
		return `${name} (account unavailable)`;
	}
	return name;
}

function serviceAdvisory(
	requiredServiceId: string | null | undefined,
	person: ScheduleData["people"][number] | undefined,
	serviceLabel: string,
): string | null {
	if (!requiredServiceId || !person) return null;
	if (person.serviceIds.length === 0) {
		return "No services are recorded for this person. Confirm suitability manually.";
	}
	if (!person.serviceIds.includes(requiredServiceId)) {
		return `Recorded services do not include ${serviceLabel}. Confirm before assigning.`;
	}
	return null;
}

function emptyVisit(date: string): SaveScheduleVisitInput {
	return {
		jobId: "",
		date,
		startTime: "09:00",
		endTime: "10:00",
		phase: "",
		requiredServiceId: null,
		status: "planned",
		accessStatus: "unknown",
		accessNote: "",
		accessWindowAcknowledged: false,
		assignments: [],
	};
}

/** Strips a stored visit down to the editable draft the save API accepts. */
function visitToDraft(visit: ScheduledVisit): SaveScheduleVisitInput {
	return {
		id: visit.id,
		jobId: visit.jobId,
		date: visit.date,
		startTime: visit.startTime,
		endTime: visit.endTime,
		phase: visit.phase,
		requiredServiceId: visit.requiredServiceId,
		status: visit.status,
		accessStatus: visit.accessStatus,
		accessNote: visit.accessNote,
		accessWindowAcknowledged: visit.accessWindowAcknowledged,
		revision: visit.revision,
		assignments: visit.assignments.map(
			({ personId, startTime, endTime, travelBeforeMinutes }) => ({
				personId,
				startTime,
				endTime,
				travelBeforeMinutes,
			}),
		),
	};
}

type LeaveDraft = Omit<StaffUnavailability, "id" | "personName"> & {
	id?: string;
};

function accessWarningForDraft(
	draft: SaveScheduleVisitInput,
	job: PlannerJob | undefined,
): string | null {
	const windows =
		job?.accessWindows.filter((window) => window.date === draft.date) ?? [];
	if (windows.length === 0) return null;
	if (
		windows.some(
			(window) =>
				window.startTime <= draft.startTime && draft.endTime <= window.endTime,
		)
	) {
		return null;
	}
	return `Visit ${draft.startTime}–${draft.endTime} is outside recorded access ${windows
		.map((window) => `${window.startTime}–${window.endTime}`)
		.join(", ")}.`;
}

function kindLabel(kind: string): string {
	return KIND_LABELS[kind] ?? kind;
}

/** The job label a visit chip/agenda row leads with (property, not the client). */
function jobLabel(job: PlannerJob | undefined): string {
	return job?.propertyAddress || job?.title || "Unknown job";
}

function requestedTimingLabel(job: PlannerJob): string {
	const completion = job.requestedCompletionOn
		? `${job.timingRequirement} ${formatDate(job.requestedCompletionOn)}`
		: job.timingRequirement;
	const notBefore = job.requestedNotBeforeOn
		? ` · Not before ${formatDate(job.requestedNotBeforeOn)}`
		: "";
	return `Requested timing: ${completion}${notBefore}`;
}

interface CalendarProps {
	month: string;
	view: "month" | "week";
	mode: "jobs" | "staff";
	selectedDate: string;
	selectedJobId: string | null;
	visitsByDate: Map<string, ScheduledVisit[]>;
	jobsById: Map<string, PlannerJob>;
	conflictingVisitIds: Set<string>;
	preview: PreviewApi;
	onSelectDate: (date: string) => void;
	onSelectVisit: (date: string, jobId: string) => void;
}

/** The month grid: weekday heads, 42 day cells, up to three visit chips each. */
function MonthCalendar({
	month,
	view,
	mode,
	selectedDate,
	selectedJobId,
	visitsByDate,
	jobsById,
	conflictingVisitIds,
	preview,
	onSelectDate,
	onSelectVisit,
}: CalendarProps) {
	const dates = view === "month" ? monthGrid(month) : weekGrid(selectedDate);
	return (
		<section
			className={cn(styles.calendar, view === "week" && styles.weekCalendar)}
			aria-label={`${view === "month" ? "Monthly" : "Weekly"} visit calendar`}
		>
			{WEEKDAYS.map((day) => (
				<strong key={day}>{day}</strong>
			))}
			{dates.map((date, index) => {
				const visits = visitsByDate.get(date) ?? [];
				const outside = view === "month" && date.slice(0, 7) !== month;
				return (
					<div
						key={date}
						className={cn(
							styles.day,
							index % 7 > 4 && styles.weekend,
							date === isoToday() && styles.today,
							outside && styles.outside,
							date === selectedDate && styles.selected,
						)}
						aria-current={date === isoToday() ? "date" : undefined}
					>
						<button
							type="button"
							className={styles.date}
							aria-label={formatDate(date)}
							aria-pressed={date === selectedDate}
							onClick={() => onSelectDate(date)}
						>
							{Number(date.slice(8))}
						</button>
						{visits.slice(0, 3).map((visit) => (
							<button
								type="button"
								key={visit.id}
								className={cn(
									styles.visit,
									visit.status !== "planned" && styles[visit.status],
									visit.assignments.length === 0 &&
										visit.status !== "cancelled" &&
										styles.unassigned,
									selectedJobId === visit.jobId && styles.relatedVisit,
								)}
								data-visit-chip=""
								// The native tooltip is replaced by the preview card, which
								// carries the same summary and everything the chip truncates.
								aria-label={`${STATUS_LABELS[visit.status]} · ${visit.startTime}–${visit.endTime} · ${jobLabel(jobsById.get(visit.jobId))}`}
								aria-describedby={
									preview.activeId === visit.id ? PREVIEW_ID : undefined
								}
								onClick={() => onSelectVisit(date, visit.jobId)}
								onMouseEnter={(event) =>
									preview.hover(visit.id, event.currentTarget)
								}
								onMouseLeave={preview.endHover}
								onFocus={(event) => preview.show(visit.id, event.currentTarget)}
								onBlur={preview.close}
								// Touch and pen have no hover, so the tap that selects the day
								// also opens the card (and a second tap puts it away).
								onPointerDown={(event) => {
									if (event.pointerType !== "mouse") {
										preview.toggle(visit.id, event.currentTarget);
									}
								}}
							>
								<span className={styles.chipTitle}>
									{visit.startTime} ·{" "}
									{mode === "jobs"
										? jobLabel(jobsById.get(visit.jobId))
										: visit.assignments.length
											? visit.assignments.map(assignmentPersonLabel).join(", ")
											: "Unassigned"}
								</span>
								<span className={styles.chipDetail}>
									{mode === "jobs" ? (
										<>
											{visit.phase || "Site visit"} ·{" "}
											{visit.assignments.length
												? visit.assignments.map(assignmentPersonLabel).join(", ")
												: "Unassigned"}
										</>
									) : (
										<>{jobLabel(jobsById.get(visit.jobId))}</>
									)}
								</span>
								{visit.status !== "planned" && (
									<span className={styles.visitStatus}>
										{STATUS_LABELS[visit.status]}
									</span>
								)}
								{visit.accessStatus === "issue" && (
									<span className={styles.visitStatus}>Access issue</span>
								)}
								{visit.accessWindowWarning && (
									<span className={styles.visitStatus}>Access warning</span>
								)}
								{conflictingVisitIds.has(visit.id) && (
									<span className={styles.visitStatus}>Leave conflict</span>
								)}
							</button>
						))}
						{visits.length > 3 && (
							<button
								type="button"
								className={styles.more}
								onClick={() => onSelectDate(date)}
							>
								+{visits.length - 3} more
							</button>
						)}
					</div>
				);
			})}
		</section>
	);
}

interface InspectorJobsProps {
	visits: ScheduledVisit[];
	jobsById: Map<string, PlannerJob>;
	servicesById: Map<string, ScheduleData["services"][number]>;
	canWrite: boolean;
	onEditVisit: (visit: ScheduledVisit) => void;
	conflicts: ScheduleData["conflicts"];
}

/** Selected-day agenda in Jobs mode plus the Needs planning list. */
function JobsInspector({
	visits,
	jobsById,
	servicesById,
	canWrite,
	onEditVisit,
	conflicts,
}: InspectorJobsProps) {
	return (
		<>
			{visits.length === 0 && (
				<p className={styles.quiet}>No visits planned for this day.</p>
			)}
			{visits.map((visit) => (
				<div className={styles.visitRow} key={visit.id}>
					<span className={styles.rowTime}>
						{visit.startTime}–{visit.endTime}
					</span>
					<h3>{jobLabel(jobsById.get(visit.jobId))}</h3>
					<p className={styles.quiet}>
						{jobsById.get(visit.jobId)?.clientName}
					</p>
					<p>
						{visit.startTime}–{visit.endTime} · {visit.phase || "Visit"} ·{" "}
						{STATUS_LABELS[visit.status]}
					</p>
					<p>
						Trade: {visit.requiredServiceId
							? servicesById.get(visit.requiredServiceId)?.label || "Unknown service"
							: "Not specified"}
					</p>
					{visit.accessWindowWarning && (
						<p className={styles.warningText}>
							{visit.accessWindowWarning}
							{visit.accessWindowAcknowledged ? " — acknowledged" : ""}
						</p>
					)}
					{conflicts
						.filter((conflict) => conflict.visitId === visit.id)
						.map((conflict) => (
							<p className={styles.warningText} key={conflict.personId}>
								{conflict.personName}: {conflict.kind === "travel" ? "travel reserve" : "assignment"}{" "}
								{conflict.startTime}–
								{conflict.endTime} conflicts with leave {conflict.conflictStartTime}–
								{conflict.conflictEndTime}
							</p>
						))}
					<p>
						{visit.accessStatus === "unknown"
							? "Access unknown"
							: visit.accessStatus === "issue"
								? "Access issue"
								: "Access confirmed"}
						{visit.accessNote ? ` — ${visit.accessNote}` : ""}
					</p>
					<p>
						{visit.assignments.length
							? visit.assignments.map(assignmentPersonLabel).join(", ")
							: "Unassigned"}
					</p>
					{canWrite && (
						<button
							type="button"
							className={styles.secondaryButton}
							onClick={() => onEditVisit(visit)}
						>
							Edit visit
						</button>
					)}
				</div>
			))}
		</>
	);
}

interface InspectorStaffProps {
	date: string;
	people: SchedulingPerson[];
	visits: ScheduledVisit[];
	unavailability: ScheduleData["unavailability"];
	jobsById: Map<string, PlannerJob>;
	conflicts: ScheduleData["conflicts"];
	canWrite: boolean;
	onAddLeave: (personId: string, date: string) => void;
	onEditLeave: (leave: StaffUnavailability) => void;
}

/** Selected person's chronological day: work at different properties, travel, leave. */
function StaffInspector({
	date,
	people,
	visits,
	unavailability,
	jobsById,
	conflicts,
	canWrite,
	onAddLeave,
	onEditLeave,
}: InspectorStaffProps) {
	const [personId, setPersonId] = useState("");
	const person =
		people.find((candidate) => candidate.id === personId) ?? people[0];
	const dayVisits = visits.filter((visit) => visit.status !== "cancelled");
	const assignments = person
		? dayVisits.flatMap((visit) =>
				visit.assignments
					.filter((assignment) => assignment.personId === person.id)
					.map((assignment) => ({ visit, assignment })),
			)
		: [];
	const leave = person
		? unavailability.filter(
				(item) => item.personId === person.id && item.date === date,
			)
		: [];

	return (
		<>
			<label className={styles.personPicker}>
				Person
				<select
					value={person?.id ?? ""}
					onChange={(event) => setPersonId(event.target.value)}
				>
					{people.map((candidate) => (
						<option key={candidate.id} value={candidate.id}>
							{candidate.name} ({kindLabel(candidate.kind)})
						</option>
					))}
				</select>
			</label>
			{!person && (
				<p className={styles.quiet}>No people can be assigned yet.</p>
			)}
			{person && canWrite && (
				<button
					type="button"
					className={styles.secondaryButton}
					onClick={() => onAddLeave(person.id, date)}
				>
					Record leave
				</button>
			)}
			{assignments.map(({ visit, assignment }) => (
				<div
					className={styles.visitRow}
					key={`${visit.id}-${assignment.personId}`}
				>
					<h3>{jobLabel(jobsById.get(visit.jobId))}</h3>
					<p>
						{assignment.startTime}–{assignment.endTime}
						{assignment.travelBeforeMinutes > 0
							? ` · ${assignment.travelBeforeMinutes} min travel before`
							: ""}
					</p>
					<p className={styles.quiet}>
						{visit.startTime}–{visit.endTime} visit window
					</p>
					{conflicts.some(
						(conflict) =>
							conflict.visitId === visit.id &&
							conflict.personId === assignment.personId,
					) && (
						<p className={styles.warningText}>
							{conflicts.find(
								(conflict) =>
									conflict.visitId === visit.id &&
									conflict.personId === assignment.personId,
							)?.kind === "travel"
								? "Travel reserve conflicts with recorded leave"
								: "Assignment conflicts with recorded leave"}
						</p>
					)}
				</div>
			))}
			{leave.map((item) => (
				<div className={styles.visitRow} key={item.id}>
					<h3>Recorded leave</h3>
					<p>
						{item.startTime}–{item.endTime}
						{item.note ? ` — ${item.note}` : ""}
					</p>
					{canWrite && (
						<button
							type="button"
							className={styles.secondaryButton}
							onClick={() => onEditLeave(item)}
						>
							Edit leave
						</button>
					)}
				</div>
			))}
			{person && assignments.length === 0 && leave.length === 0 && (
				<p className={styles.quiet}>
					No work or recorded leave for this day. An empty day is not a promise
					of availability.
				</p>
			)}
			{assignments.length > 1 && (
				<p className={styles.quiet}>
					Work at different properties on the same day — plan travel between.
				</p>
			)}
		</>
	);
}

/**
 * The internal scheduling planner at `/schedule`: a month calendar with a
 * selected-day inspector, and one visit editor covering jobs, times, access
 * and staff assignment intervals. Visits are internal-only — nothing here
 * touches job stages, Schedule Intent, members, or portal data.
 */
export function SchedulingPage() {
	const { user } = useAuth();
	const canWrite = Boolean(
		user?.permissions.some(
			(permission) => permission === "*" || permission === "jobs.write",
		),
	);
	const [loading, setLoading] = useState(true);
	const [month, setMonth] = useState(() => isoToday().slice(0, 7));
	const [selectedDate, setSelectedDate] = useState(() => isoToday());
	const [view, setView] = useState<"month" | "week">("month");
	const [mode, setMode] = useState<"jobs" | "staff">("jobs");
	const [jobFilter, setJobFilter] = useState("");
	const [staffFilter, setStaffFilter] = useState("");
	const [tradeFilter, setTradeFilter] = useState("");
	const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
	const [data, setData] = useState<ScheduleData | null>(null);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [draft, setDraft] = useState<SaveScheduleVisitInput | null>(null);
	const [leaveDraft, setLeaveDraft] = useState<LeaveDraft | null>(null);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [sidebarTab, setSidebarTab] = useState<"day" | "planning">("day");
	const [previewState, setPreviewState] = useState<{
		visitId: string;
		anchor: DOMRect;
		touch: boolean;
	} | null>(null);
	const hoverTimer = useRef<number | null>(null);
	// Guards against a slow range response landing after a newer one.
	const loadSequence = useRef(0);
	const selectedWeekStart = weekBounds(selectedDate).from;

	const load = useCallback(async () => {
		const sequence = ++loadSequence.current;
		const { from, to } =
			view === "month" ? monthBounds(month) : weekBounds(selectedWeekStart);
		setLoadError(null);
		setLoading(true);
		try {
			const next = await getSchedule(from, to);
			if (loadSequence.current === sequence) setData(next);
		} catch (cause) {
			if (loadSequence.current === sequence) {
				setLoadError(
					cause instanceof Error
						? cause.message
						: "Could not load the schedule",
				);
			}
		} finally {
			if (loadSequence.current === sequence) setLoading(false);
		}
	}, [month, selectedWeekStart, view]);

	useEffect(() => {
		void load();
		return () => {
			++loadSequence.current;
		};
	}, [load]);

	const cancelHover = useCallback((): void => {
		if (hoverTimer.current !== null) {
			window.clearTimeout(hoverTimer.current);
			hoverTimer.current = null;
		}
	}, []);
	useEffect(() => cancelHover, [cancelHover]);
	// A card is anchored to a chip's viewport rect, so anything that re-lays out
	// the grid — a new range, a different chip set — invalidates it.
	useEffect(() => {
		setPreviewState(null);
	}, [data, view, mode, jobFilter, staffFilter, tradeFilter]);

	const preview = useMemo<PreviewApi>(
		() => ({
			activeId: previewState?.visitId ?? null,
			hover(visitId, element) {
				cancelHover();
				const anchor = element.getBoundingClientRect();
				hoverTimer.current = window.setTimeout(
					() => setPreviewState({ visitId, anchor, touch: false }),
					140,
				);
			},
			endHover() {
				cancelHover();
				// A card a tap opened outlives the synthetic mouse events that
				// follow that tap; only a real pointer leaving closes it.
				setPreviewState((current) => (current?.touch ? current : null));
			},
			show(visitId, element) {
				cancelHover();
				setPreviewState({
					visitId,
					anchor: element.getBoundingClientRect(),
					touch: false,
				});
			},
			toggle(visitId, element) {
				cancelHover();
				setPreviewState((current) =>
					current?.visitId === visitId
						? null
						: {
								visitId,
								anchor: element.getBoundingClientRect(),
								touch: true,
							},
				);
			},
			close() {
				cancelHover();
				setPreviewState(null);
			},
		}),
		[cancelHover, previewState?.visitId],
	);

	const jobsById = useMemo(
		() => new Map((data?.jobs ?? []).map((job) => [job.id, job])),
		[data],
	);
	const servicesById = useMemo(
		() => new Map((data?.services ?? []).map((service) => [service.id, service])),
		[data],
	);
	const filteredVisits = useMemo(
		() =>
			(data?.visits ?? []).filter(
				(visit) =>
					(!jobFilter || visit.jobId === jobFilter) &&
					(!staffFilter ||
						visit.assignments.some(
							(assignment) => assignment.personId === staffFilter,
						)) &&
					(!tradeFilter || visit.requiredServiceId === tradeFilter),
			),
		[data, jobFilter, staffFilter, tradeFilter],
	);

	const visitsByDate = useMemo(() => {
		const groups = new Map<string, ScheduledVisit[]>();
		for (const visit of filteredVisits) {
			const group = groups.get(visit.date) ?? [];
			group.push(visit);
			groups.set(visit.date, group);
		}
		return groups;
	}, [filteredVisits]);
	const conflictingVisitIds = useMemo(
		() => new Set((data?.conflicts ?? []).map((conflict) => conflict.visitId)),
		[data],
	);

	const needsPlanning = useMemo(
		() =>
			(data?.jobs ?? []).filter(
				(job) =>
					job.status !== "done" &&
					(job.visitCount === 0 ||
						job.unassignedVisitCount > 0 ||
						(job.estimatedStaffMinutes !== null &&
							job.allocatedStaffMinutes < job.estimatedStaffMinutes)),
			),
		[data],
	);

	const changeMonth = (next: string): void => {
		if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(next)) return;
		setMonth(next);
		setSelectedDate(
			next === isoToday().slice(0, 7) ? isoToday() : `${next}-01`,
		);
	};
	const changeRange = (delta: number): void => {
		if (view === "month") {
			changeMonth(shiftMonth(month, delta));
			return;
		}
		const nextDate = shiftDate(selectedDate, delta * 7);
		setSelectedDate(nextDate);
		setMonth(nextDate.slice(0, 7));
	};
	/**
	 * Tablist keyboard contract (WAI-ARIA): the arrows move between the two
	 * panels, Tab leaves the list. Activation follows focus — both panels are
	 * cheap, so there is nothing to be gained by making the user confirm.
	 */
	const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		const moves: Record<string, "day" | "planning"> = {
			ArrowLeft: "day",
			ArrowRight: "planning",
			Home: "day",
			End: "planning",
		};
		const next = moves[event.key];
		if (!next) return;
		event.preventDefault();
		setSidebarTab(next);
		document.getElementById(`schedule-tab-${next}`)?.focus();
	};

	const selectVisit = (date: string, jobId: string): void => {
		setSelectedDate(date);
		setSelectedJobId(jobId);
	};

	const openCreate = (date: string, jobId = ""): void => {
		if (!canWrite || loading || loadError) return;
		setSaveError(null);
		setDraft({ ...emptyVisit(date), jobId });
	};

	const openEdit = (visit: ScheduledVisit): void => {
		if (!canWrite || loading || loadError) return;
		setSaveError(null);
		setDraft(visitToDraft(visit));
	};

	const openAddLeave = (personId: string, date: string): void => {
		if (!canWrite || loading || loadError) return;
		setSaveError(null);
		setLeaveDraft({
			personId,
			date,
			startTime: "09:00",
			endTime: "17:00",
			note: "",
		});
	};

	const openEditLeave = (leave: StaffUnavailability): void => {
		if (!canWrite || loading || loadError) return;
		setSaveError(null);
		setLeaveDraft({
			id: leave.id,
			personId: leave.personId,
			date: leave.date,
			startTime: leave.startTime,
			endTime: leave.endTime,
			note: leave.note,
		});
	};

	const closeDraft = (): void => {
		if (saving) return;
		setDraft(null);
		setSaveError(null);
	};
	const closeLeaveDraft = (): void => {
		if (saving) return;
		setLeaveDraft(null);
		setSaveError(null);
	};

	const saveDraft = async (event: FormEvent): Promise<void> => {
		event.preventDefault();
		if (!draft || saving) return;
		setSaving(true);
		setSaveError(null);
		try {
			await saveScheduleVisit(draft);
			setSelectedDate(draft.date);
			setDraft(null);
			if (draft.date.slice(0, 7) !== month) setMonth(draft.date.slice(0, 7));
			else await load();
		} catch (cause) {
			// Stays inside the dialog so the entered values and the reason are
			// both visible (conflict messages are the point of this editor).
			setSaveError(
				cause instanceof Error ? cause.message : "Could not save the visit",
			);
		} finally {
			setSaving(false);
		}
	};

	const saveLeaveDraft = async (event: FormEvent): Promise<void> => {
		event.preventDefault();
		if (!leaveDraft || saving) return;
		setSaving(true);
		setSaveError(null);
		try {
			await saveStaffUnavailability(leaveDraft);
			setSelectedDate(leaveDraft.date);
			setLeaveDraft(null);
			if (leaveDraft.date.slice(0, 7) !== month) {
				setMonth(leaveDraft.date.slice(0, 7));
			} else {
				await load();
			}
		} catch (cause) {
			setSaveError(
				cause instanceof Error ? cause.message : "Could not save the leave",
			);
		} finally {
			setSaving(false);
		}
	};

	const patchDraft = (patch: Partial<SaveScheduleVisitInput>): void => {
		setDraft((current) => (current ? { ...current, ...patch } : current));
	};

	const patchAssignment = (
		index: number,
		patch: Partial<SaveScheduleVisitInput["assignments"][number]>,
	): void => {
		setDraft((current) =>
			current
				? {
						...current,
						assignments: current.assignments.map((assignment, i) =>
							i === index ? { ...assignment, ...patch } : assignment,
						),
					}
				: current,
		);
	};

	const removeAssignment = (index: number): void => {
		setDraft((current) =>
			current
				? {
						...current,
						assignments: current.assignments.filter((_, i) => i !== index),
					}
				: current,
		);
	};

	const addAssignment = (): void => {
		setDraft((current) =>
			current
				? {
						...current,
						assignments: [
							...current.assignments,
							{
								personId: "",
								startTime: current.startTime,
								endTime: current.endTime,
								travelBeforeMinutes: 0,
							},
						],
					}
				: current,
		);
	};

	if (loadError && !data) {
		return (
			<section className={styles.page}>
				<div className={styles.error} role="alert">
					{loadError}{" "}
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={() => void load()}
					>
						Retry
					</button>
				</div>
			</section>
		);
	}
	if (!data) {
		return (
			<section className={styles.page}>
				<div className={styles.empty}>Loading planner…</div>
			</section>
		);
	}

	const selectedVisits = visitsByDate.get(selectedDate) ?? [];
	const previewVisit = previewState
		? filteredVisits.find((visit) => visit.id === previewState.visitId)
		: undefined;
	const displayedDates =
		view === "month" ? monthGrid(month) : weekGrid(selectedDate);
	const visibleVisitCount = displayedDates.reduce(
		(total, date) => total + (visitsByDate.get(date)?.length ?? 0),
		0,
	);
	const draftAccessWarning = draft
		? accessWarningForDraft(draft, jobsById.get(draft.jobId))
		: null;

	return (
		<section className={styles.page}>
			{/*
			 * The page keeps a heading for assistive technology and document
			 * outline only: the planner's own controls say where you are, and the
			 * calendar is worth the vertical space a title block would take.
			 */}
			<h1 className="visually-hidden">Schedule</h1>
			{!canWrite && (
				<p className={styles.readOnlyNotice} role="status">
					Read-only schedule. You can inspect visits, staff and conflicts; changes
					require scheduling write access.
				</p>
			)}

			{loadError && (
				<div className={styles.error} role="alert">
					{loadError}{" "}
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={() => void load()}
					>
						Retry
					</button>
				</div>
			)}

			{/*
			 * One control line. Reading order is the order of the work: move
			 * through time, choose the span, choose what a day shows, narrow the
			 * set, then act. Groups are separated by a hairline rather than by
			 * their own row, because the calendar under them is the screen.
			 */}
			<div className={styles.toolbar}>
				<div className={styles.rangeNav}>
					<button
						type="button"
						className={styles.navButton}
						aria-label={`Previous ${view}`}
						onClick={() => changeRange(-1)}
					>
						‹
					</button>
					<input
						className={styles.monthInput}
						aria-label="Month"
						min="1900-01"
						max="2100-12"
						type="month"
						value={month}
						onChange={(event) =>
							event.target.value && changeMonth(event.target.value)
						}
					/>
					<button
						type="button"
						className={styles.navButton}
						aria-label={`Next ${view}`}
						onClick={() => changeRange(1)}
					>
						›
					</button>
				</div>
				<button
					type="button"
					className={styles.todayButton}
					onClick={() => {
						const today = isoToday();
						setMonth(today.slice(0, 7));
						setSelectedDate(today);
					}}
				>
					Today
				</button>
				<span className={styles.divider} aria-hidden="true" />
				<fieldset className={styles.switch}>
					<legend className="visually-hidden">Calendar view</legend>
					<button
						type="button"
						aria-pressed={view === "month"}
						onClick={() => setView("month")}
					>
						Month
					</button>
					<button
						type="button"
						aria-pressed={view === "week"}
						onClick={() => setView("week")}
					>
						Week
					</button>
				</fieldset>
				<fieldset className={styles.switch}>
					<legend className="visually-hidden">Day details</legend>
					<button
						type="button"
						aria-pressed={mode === "jobs"}
						onClick={() => setMode("jobs")}
					>
						Jobs
					</button>
					<button
						type="button"
						aria-pressed={mode === "staff"}
						onClick={() => setMode("staff")}
					>
						Staff
					</button>
				</fieldset>
				<span className={styles.divider} aria-hidden="true" />
				<fieldset className={styles.filters}>
					<legend className="visually-hidden">Schedule filters</legend>
					<select
						className={styles.filterSelect}
						aria-label="Filter by job"
						data-active={jobFilter ? "true" : undefined}
						value={jobFilter}
						onChange={(event) => {
							setJobFilter(event.target.value);
							setSelectedJobId(event.target.value || null);
						}}
					>
						<option value="">All jobs</option>
						{data.jobs.map((job) => (
							<option key={job.id} value={job.id}>
								{jobLabel(job)}
							</option>
						))}
					</select>
					<select
						className={styles.filterSelect}
						aria-label="Filter by person"
						data-active={staffFilter ? "true" : undefined}
						value={staffFilter}
						onChange={(event) => setStaffFilter(event.target.value)}
					>
						<option value="">All people</option>
						{data.people.map((person) => (
							<option key={person.id} value={person.id}>
								{person.name}
							</option>
						))}
					</select>
					<select
						className={styles.filterSelect}
						aria-label="Filter by trade"
						data-active={tradeFilter ? "true" : undefined}
						value={tradeFilter}
						onChange={(event) => setTradeFilter(event.target.value)}
					>
						<option value="">All trades</option>
						{data.services.map((service) => (
							<option key={service.id} value={service.id}>
								{service.label}
							</option>
						))}
					</select>
					{(jobFilter || staffFilter || tradeFilter) && (
						<button
							type="button"
							className={styles.clearFilters}
							onClick={() => {
								setJobFilter("");
								setStaffFilter("");
								setTradeFilter("");
								setSelectedJobId(null);
							}}
						>
							Clear
						</button>
					)}
				</fieldset>
				{/* What the range holds, then the one action, as one right-hand block. */}
				<div className={styles.trailing}>
					<p className={styles.rangeStatus} role="status" aria-live="polite">
						{loading
							? "Loading visits…"
							: loadError
								? "Refresh failed — retry before planning."
								: `${visibleVisitCount} ${visibleVisitCount === 1 ? "visit" : "visits"} this ${view} · Adelaide time`}
					</p>
					{canWrite && (
						<button
							type="button"
							className={styles.primaryButton}
							disabled={loading || Boolean(loadError)}
							onClick={() => openCreate(selectedDate)}
						>
							Schedule visit
						</button>
					)}
				</div>
			</div>
			<div className={styles.layout} aria-busy={loading}>
				<div
					className={styles.calendarWrap}
					role="region"
					aria-label="Schedule calendar; scroll horizontally on narrow screens"
					tabIndex={0}
				>
					<MonthCalendar
						month={month}
						view={view}
						mode={mode}
						selectedDate={selectedDate}
						selectedJobId={selectedJobId}
						visitsByDate={loading || loadError ? new Map() : visitsByDate}
						jobsById={jobsById}
						conflictingVisitIds={conflictingVisitIds}
						preview={preview}
						onSelectDate={setSelectedDate}
						onSelectVisit={selectVisit}
					/>
				</div>
				{/*
				 * Two panels, one column. The selected day and the planning backlog
				 * are read alternately, never together, and the backlog below the
				 * calendar was a scroll away from the grid it is planned onto.
				 */}
				<aside className={styles.inspector}>
					<div
						className={styles.tabs}
						role="tablist"
						aria-label="Planner details"
						onKeyDown={onTabKeyDown}
					>
						<button
							type="button"
							role="tab"
							id="schedule-tab-day"
							className={styles.tab}
							aria-selected={sidebarTab === "day"}
							aria-controls="schedule-panel-day"
							tabIndex={sidebarTab === "day" ? 0 : -1}
							onClick={() => setSidebarTab("day")}
						>
							Day details
						</button>
						<button
							type="button"
							role="tab"
							id="schedule-tab-planning"
							className={styles.tab}
							aria-selected={sidebarTab === "planning"}
							aria-controls="schedule-panel-planning"
							tabIndex={sidebarTab === "planning" ? 0 : -1}
							onClick={() => setSidebarTab("planning")}
						>
							Needs planning
							<span className={styles.count}>{needsPlanning.length}</span>
						</button>
					</div>
					{sidebarTab === "day" ? (
						<div
							className={styles.tabPanel}
							role="tabpanel"
							id="schedule-panel-day"
							aria-labelledby="schedule-tab-day"
							tabIndex={0}
						>
							<div className={styles.inspectorHead}>
								<h2>{formatDate(selectedDate)}</h2>
								<p className={styles.quiet}>Internal visits · Adelaide time</p>
							</div>
							{loading || loadError ? (
								<p className={styles.empty}>
									{loading
										? "Loading visits…"
										: "Day details unavailable until refresh succeeds."}
								</p>
							) : mode === "staff" ? (
								<StaffInspector
									date={selectedDate}
									people={
										staffFilter
											? data.people.filter((person) => person.id === staffFilter)
											: data.people
									}
									visits={selectedVisits}
									unavailability={data.unavailability}
									jobsById={jobsById}
									conflicts={data.conflicts}
									canWrite={canWrite}
									onAddLeave={openAddLeave}
									onEditLeave={openEditLeave}
								/>
							) : (
								<JobsInspector
									visits={selectedVisits}
									jobsById={jobsById}
									servicesById={servicesById}
									canWrite={canWrite}
									onEditVisit={openEdit}
									conflicts={data.conflicts}
								/>
							)}
						</div>
					) : (
						<div
							className={styles.tabPanel}
							role="tabpanel"
							id="schedule-panel-planning"
							aria-labelledby="schedule-tab-planning"
							tabIndex={0}
						>
							<div className={styles.inspectorHead}>
								<h2>Unscheduled and understaffed</h2>
								<p className={styles.quiet}>
									Work with no visit, no crew or hours still to cover
								</p>
							</div>
							{needsPlanning.length === 0 && (
								<p className={styles.empty}>No outstanding planning gaps.</p>
							)}
							{needsPlanning.map((job) => (
								<div className={styles.planningRow} key={job.id}>
									<h3>{jobLabel(job)}</h3>
									<p>{job.title}</p>
									<p className={styles.quiet}>{job.clientName}</p>
									<p className={styles.quiet}>{requestedTimingLabel(job)}</p>
									<div className={styles.planningActions}>
										<span className={styles.planningState}>
											{job.visitCount === 0
												? "Unscheduled"
												: job.unassignedVisitCount > 0
													? "Staff needed"
													: `${formatStaffHours(Math.max(0, (job.estimatedStaffMinutes ?? 0) - job.allocatedStaffMinutes))} remaining`}
										</span>
										{canWrite && (
											<button
												className={styles.secondaryButton}
												disabled={loading || Boolean(loadError)}
												type="button"
												onClick={() => openCreate(selectedDate, job.id)}
											>
												Schedule
											</button>
										)}
									</div>
								</div>
							))}
						</div>
					)}
				</aside>
			</div>
			{previewVisit && previewState && (
				<VisitPreviewCard
					id={PREVIEW_ID}
					visit={previewVisit}
					job={jobsById.get(previewVisit.jobId)}
					service={
						previewVisit.requiredServiceId
							? servicesById.get(previewVisit.requiredServiceId)
							: undefined
					}
					conflicts={data.conflicts.filter(
						(conflict) => conflict.visitId === previewVisit.id,
					)}
					anchor={previewState.anchor}
					touch={previewState.touch}
					onDismiss={preview.close}
				/>
			)}
			{draft && (
				<Modal
					title={draft.id ? "Edit visit" : "Add visit"}
					subtitle={
						draft.jobId
							? jobLabel(jobsById.get(draft.jobId))
							: "Choose a property, set the time and assign your team."
					}
					size="lg"
					onClose={closeDraft}
					footer={
						<>
							<button
								type="button"
								className={styles.secondaryButton}
								disabled={saving}
								onClick={closeDraft}
							>
								Cancel
							</button>
							<button
								type="submit"
								className={styles.primaryButton}
								form="visit-editor"
								disabled={saving}
							>
								{saving ? "Saving…" : "Save visit"}
							</button>
						</>
					}
				>
					<form
						id="visit-editor"
						className={styles.editor}
						aria-busy={saving}
						onSubmit={saveDraft}
					>
						{saveError && (
							<p className={styles.error} role="alert">
								{saveError}
							</p>
						)}
						<label>
							Job
							<select
								required
								value={draft.jobId}
								onChange={(event) => patchDraft({ jobId: event.target.value })}
							>
								<option value="">Choose job</option>
								{data.jobs.map((job) => (
									<option key={job.id} value={job.id}>
										{job.title} — {job.propertyAddress}
									</option>
								))}
							</select>
						</label>
						<div className={styles.times}>
							<label>
								Date
								<input
									required
									type="date"
									value={draft.date}
									onChange={(event) => patchDraft({ date: event.target.value })}
								/>
							</label>
							<label>
								Status
								<select
									value={draft.status}
									onChange={(event) =>
										patchDraft({
											status: event.target
												.value as SaveScheduleVisitInput["status"],
										})
									}
								>
									<option value="planned">Planned</option>
									<option value="tentative">Tentative</option>
									<option value="cancelled">Cancelled</option>
								</select>
							</label>
						</div>
						<div className={styles.times}>
							<label>
								Start
								<input
									required
									type="time"
									value={draft.startTime}
									onChange={(event) =>
										patchDraft({ startTime: event.target.value })
									}
								/>
							</label>
							<label>
								End
								<input
									required
									type="time"
									value={draft.endTime}
									onChange={(event) =>
										patchDraft({ endTime: event.target.value })
									}
								/>
							</label>
						</div>
						<label>
							Phase
							<input
								maxLength={120}
								value={draft.phase}
								onChange={(event) => patchDraft({ phase: event.target.value })}
							/>
						</label>
						<label>
							Required service
							<select
								value={draft.requiredServiceId ?? ""}
								onChange={(event) =>
									patchDraft({ requiredServiceId: event.target.value || null })
								}
							>
								<option value="">No service specified</option>
								{data.services.map((service) => (
									<option key={service.id} value={service.id}>
										{service.category} — {service.label}
									</option>
								))}
							</select>
						</label>
						<label>
							Access
							<select
								value={draft.accessStatus}
								onChange={(event) =>
									patchDraft({
										accessStatus: event.target
											.value as SaveScheduleVisitInput["accessStatus"],
									})
								}
							>
								<option value="unknown">Unknown</option>
								<option value="confirmed">Confirmed</option>
								<option value="issue">Issue</option>
							</select>
						</label>
						<label>
							Access note
							<textarea
								maxLength={1000}
								rows={2}
								value={draft.accessNote}
								onChange={(event) =>
									patchDraft({ accessNote: event.target.value })
								}
							/>
						</label>
						{draft.accessStatus === "unknown" && (
							<p className={styles.warningText} role="status">
								Access is unknown. This visit is not a confirmed booking.
							</p>
						)}
						{draftAccessWarning && (
							<div className={styles.warningBox} role="alert">
								<p>{draftAccessWarning}</p>
								<label className={styles.acknowledgement}>
									<input
										type="checkbox"
										checked={draft.accessWindowAcknowledged === true}
										onChange={(event) =>
											patchDraft({
												accessWindowAcknowledged: event.target.checked,
											})
										}
									/>
									I acknowledge this visit is outside the recorded access window.
								</label>
							</div>
						)}

						<div className={styles.peopleHead}>
							<h3>People &amp; travel</h3>
							<p className={styles.quiet}>
								Each person can work all or part of this visit. Travel is
								reserved before their start time.
							</p>
						</div>
						{draft.assignments.map((assignment, index) => {
							const person = data.people.find(
								(candidate) => candidate.id === assignment.personId,
							);
							const requiredService = data.services.find(
								(service) => service.id === draft.requiredServiceId,
							);
							const advisory = serviceAdvisory(
								draft.requiredServiceId,
								person,
								requiredService?.label ?? "the required service",
							);
							return (
								<div
									className={styles.assignment}
									// biome-ignore lint/suspicious/noArrayIndexKey: rows are append/remove only, never reordered, so the index is a stable identity for the editing session.
									key={index}
								>
									<label className={styles.personField}>
										Person
										<select
											required
											aria-label="Person"
											value={assignment.personId}
											onChange={(event) =>
												patchAssignment(index, { personId: event.target.value })
											}
										>
											<option value="">Choose person</option>
											{data.people.map((candidate) => (
												<option key={candidate.id} value={candidate.id}>
													{candidate.name} ({kindLabel(candidate.kind)})
												</option>
											))}
										</select>
									</label>
									<label>
										Start
										<input
											required
											aria-label="Assignment start"
											type="time"
											value={assignment.startTime}
											onChange={(event) =>
												patchAssignment(index, {
													startTime: event.target.value,
												})
											}
										/>
									</label>
									<label>
										End
										<input
											required
											aria-label="Assignment end"
											type="time"
											value={assignment.endTime}
											onChange={(event) =>
												patchAssignment(index, { endTime: event.target.value })
											}
										/>
									</label>
									<label>
										Travel (min)
										<input
											className={styles.travelInput}
											aria-label={
												person
													? `Travel before minutes for ${person.name}`
													: "Travel before minutes"
											}
											type="number"
											min={0}
											max={720}
											step={15}
											value={assignment.travelBeforeMinutes}
											onChange={(event) => {
												const travel = event.target.valueAsNumber;
												patchAssignment(index, {
													travelBeforeMinutes: Number.isNaN(travel)
														? 0
														: travel,
												});
											}}
										/>
									</label>
									<button
										className={styles.removeButton}
										type="button"
										aria-label={
											person ? `Remove ${person.name}` : "Remove assignment"
										}
										onClick={() => removeAssignment(index)}
									>
										Remove
									</button>
									{advisory && (
										<p className={styles.quiet} role="status">
											{advisory}
										</p>
									)}
								</div>
							);
						})}
						<button
							type="button"
							className={styles.addPerson}
							onClick={addAssignment}
						>
							Add person
						</button>
					</form>
				</Modal>
			)}
			{leaveDraft && (
				<Modal
					title={leaveDraft.id ? "Edit leave" : "Record leave"}
					subtitle="Leave is a planning constraint. Existing visits are flagged, never moved."
					onClose={closeLeaveDraft}
					footer={
						<>
							<button
								type="button"
								className={styles.secondaryButton}
								disabled={saving}
								onClick={closeLeaveDraft}
							>
								Cancel
							</button>
							<button
								type="submit"
								className={styles.primaryButton}
								form="leave-editor"
								disabled={saving}
							>
								{saving ? "Saving…" : "Save leave"}
							</button>
						</>
					}
				>
					<form
						id="leave-editor"
						className={styles.editor}
						aria-busy={saving}
						onSubmit={saveLeaveDraft}
					>
						{saveError && (
							<p className={styles.error} role="alert">
								{saveError}
							</p>
						)}
						<label>
							Person
							<select
								required
								value={leaveDraft.personId}
								onChange={(event) =>
									setLeaveDraft((current) =>
										current
											? { ...current, personId: event.target.value }
											: current,
									)
								}
							>
								{data.people.map((person) => (
									<option key={person.id} value={person.id}>
										{person.name} ({kindLabel(person.kind)})
									</option>
								))}
							</select>
						</label>
						<label>
							Date
							<input
								required
								type="date"
								value={leaveDraft.date}
								onChange={(event) =>
									setLeaveDraft((current) =>
										current ? { ...current, date: event.target.value } : current,
									)
								}
							/>
						</label>
						<div className={styles.times}>
							<label>
								Start
								<input
									required
									type="time"
									value={leaveDraft.startTime}
									onChange={(event) =>
										setLeaveDraft((current) =>
											current
												? { ...current, startTime: event.target.value }
												: current,
										)
									}
								/>
							</label>
							<label>
								End
								<input
									required
									type="time"
									value={leaveDraft.endTime}
									onChange={(event) =>
										setLeaveDraft((current) =>
											current
												? { ...current, endTime: event.target.value }
												: current,
										)
									}
								/>
							</label>
						</div>
						<label>
							Note
							<textarea
								maxLength={500}
								rows={3}
								value={leaveDraft.note}
								onChange={(event) =>
									setLeaveDraft((current) =>
										current ? { ...current, note: event.target.value } : current,
									)
								}
							/>
						</label>
					</form>
				</Modal>
			)}
		</section>
	);
}
